/**
 * 旗 house（KUMO の自社の広告だけ）の間の、作り手の「{product} の広告を出す / 出さない」（2026-10-24 money/house-rewarded・house-optout-fill）。
 * 置く所: DisplayAdsCard（今の広告の画面）の本番の画面。帯とリワードの 2 つ（どちらも既定は「出す」）。
 *
 * WHY（founder 10/24「あなたのお勧めでいきます」= 管理のおすすめ）: 作り手は KUMO の広告を出さないを選べる・選ぶのは作り手。
 *   帯を出さない = バナーの枠に何も出ない（外の広告にも戻さない）。リワードを出さない = ゲームには「今は出せない」（SDK の no_fill）・報酬なし。
 * 口: GET / PUT /v1/money/self-ads/house-hosts/:projectId（{ banner?, rewarded? }）。旗 house でないサーバーは 404 = 何も出さない
 *   （旗 on の選択は SelfAdHostChoice = hosts の口。house の間はそちらが 404 で出ない = 2 つが同時に出ることは無い）。
 * 選ぶとすぐ保存（確認のダイアログは出さない）: お金が動かない・いつでも戻せる・本番の設定（config）でもない（SelfAdHostChoice と同じ考え）。
 * 文: 作り手に分かる言葉・何が起きるかだけ（「KUMODeck がやってくれる」とは書かない）・額は書かない（帯とリワードで違う・運営が変えうる）。
 */
import { useEffect, useState } from 'react';
import type { ApiError } from '../api';
import { errorText, useT } from '../i18n';
import { getHouseHostPref, isOff, putHouseHostPref } from '../selfAdsApi';
import type { HouseHostPref } from '../selfAds';

const SLOTS = ['banner', 'rewarded'] as const;

/**
 * earn: 「初日から稼げる」の試しの作り手（DisplayAdsCard の adsCardMode が earn）。説明の「見た分だけ決まった額」は表示ごとの払いの時の文 =
 * 試しの間は払いがプレイの数え（KUMODeck の広告が出たページの 1 人 1 日 1 回）なので、その言い方に替える（2026-10-27 console/earn-ads-card）
 */
export function HouseAdsChoice({ projectId, earn = false }: { projectId: string; earn?: boolean }) {
  const { t } = useT();
  const [pref, setPref] = useState<HouseHostPref | null>(null);
  const [off, setOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setPref(null);
    getHouseHostPref(projectId)
      .then((p) => live && setPref(p))
      .catch((e) => {
        if (!live) return;
        if (isOff(e)) setOff(true);
        else setErr(errorText(e as ApiError));
      });
    return () => {
      live = false;
    };
  }, [projectId]);

  if (off) return null;
  if (!pref) return err ? <p className="alert alert-error small">{err}</p> : null;

  const save = async (body: { banner?: boolean; rewarded?: boolean }) => {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      setPref(await putHouseHostPref(projectId, body));
      setSaved(true);
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ssa-host-choice house-ads-choice">
      <fieldset className="choice-group" disabled={busy}>
        <legend>{t('ssa.house.legend')}</legend>
        <p className="small muted ssa-host-note">{t(earn ? 'pe.card.houseNote' : 'ssa.house.note')}</p>
        {SLOTS.map((slot) => (
          <label key={slot} className={`choice ${pref[slot] ? 'is-active' : ''}`}>
            <input type="checkbox" checked={pref[slot]} onChange={(e) => void save({ [slot]: e.target.checked })} />
            <span>
              <strong>{t(`ssa.house.${slot}`)}</strong>
              <span className="small muted">{t(`ssa.house.${slot}.help`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {saved && (
        <p className="small muted" aria-live="polite">
          {t('ssa.saved')}
        </p>
      )}
      {err && <p className="alert alert-error small">{err}</p>}
    </div>
  );
}
